import type { CSSProperties } from 'react';
import type { Applicant, ApplicantStatus } from '../types';
import { colors } from '../../employees/styles/personalakteStyles';

interface StatusStep {
  key: ApplicantStatus;
  label: string;
  timestampField: keyof Applicant;
}

const STEPS: StatusStep[] = [
  { key: 'NEW', label: 'Bewerbung eingegangen', timestampField: 'applied_at' },
  { key: 'CONFIRMATION_SENT', label: 'Bestätigung gesendet', timestampField: 'confirmation_sent_at' },
  { key: 'PHONE_INTERVIEW_SCHEDULED', label: 'Telefoninterview geplant', timestampField: 'phone_interview_scheduled_at' },
  { key: 'PHONE_INTERVIEW_DONE', label: 'Telefoninterview abgeschlossen', timestampField: 'phone_interview_done_at' },
  { key: 'TRIAL_SCHEDULED', label: 'Probearbeiten geplant', timestampField: 'trial_scheduled_at' },
  { key: 'TRIAL_DONE', label: 'Probearbeiten abgeschlossen', timestampField: 'trial_done_at' },
  { key: 'HIRED', label: 'Eingestellt', timestampField: 'hired_at' },
];

const STATUS_ORDER: Record<ApplicantStatus, number> = {
  NEW: 0,
  CONFIRMATION_SENT: 1,
  PHONE_INTERVIEW_SCHEDULED: 2,
  PHONE_INTERVIEW_DONE: 3,
  TRIAL_SCHEDULED: 4,
  TRIAL_DONE: 5,
  HIRED: 6,
  REJECTED: -1,
};

function formatTimestamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface StatusTimelineProps {
  applicant: Applicant;
}

export default function StatusTimeline({ applicant }: StatusTimelineProps): React.JSX.Element {
  const currentIndex = STATUS_ORDER[applicant.status] ?? -1;
  const isRejected = applicant.status === 'REJECTED';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {STEPS.map((step, i) => {
        const timestamp = applicant[step.timestampField] as string | undefined;
        const isDone = timestamp != null;
        const isCurrent = step.key === applicant.status;
        const isFuture = !isDone && !isCurrent;
        const isLast = i === STEPS.length - 1;

        let dotColor = colors.border;
        let dotBorder = colors.border;
        if (isDone) {
          dotColor = colors.success;
          dotBorder = colors.success;
        } else if (isCurrent && !isRejected) {
          dotColor = colors.slate;
          dotBorder = colors.slate;
        }

        const lineColor = isDone ? colors.success : colors.border;

        return (
          <div key={step.key} style={{ display: 'flex', gap: 12, minHeight: isLast ? 'auto' : 40 }}>
            {/* Dot + Line */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
              <div style={{
                width: isCurrent && !isRejected ? 14 : 10,
                height: isCurrent && !isRejected ? 14 : 10,
                borderRadius: '50%',
                background: dotColor,
                border: `2px solid ${dotBorder}`,
                flexShrink: 0,
                marginTop: 3,
                boxShadow: isCurrent && !isRejected ? `0 0 0 3px rgba(82, 113, 165, 0.2)` : 'none',
              }} />
              {!isLast && (
                <div style={{ width: 2, flex: 1, background: lineColor, minHeight: 16 }} />
              )}
            </div>

            {/* Text */}
            <div style={{ paddingBottom: isLast ? 0 : 8 }}>
              <div style={{
                fontSize: 13,
                fontWeight: isCurrent && !isRejected ? 600 : isDone ? 500 : 400,
                color: isFuture ? colors.textLight : colors.textBody,
              }}>
                {step.label}
              </div>
              {timestamp && (
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                  {formatTimestamp(timestamp)}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Rejected row */}
      {isRejected && (
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
            <div style={{
              width: 14, height: 14, borderRadius: '50%',
              background: colors.danger, border: `2px solid ${colors.danger}`,
              flexShrink: 0, marginTop: 3,
              boxShadow: `0 0 0 3px rgba(239, 68, 68, 0.2)`,
            }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.danger }}>
              Abgelehnt
            </div>
            {applicant.rejected_at && (
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                {formatTimestamp(applicant.rejected_at)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
